import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { reel } from "@/data/content";
import { Reveal, SectionLabel, EASE, youtubeId } from "./Reveal";

const RotatingBadge = () => (
  <svg viewBox="0 0 120 120" className="badge-spin absolute inset-0 h-full w-full">
    <defs><path id="circ" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0" /></defs>
    <text fill="#FFFFFF" fontSize="11" fontWeight="700" letterSpacing="3.2">
      <textPath href="#circ">PLAY THE REEL • PLAY THE REEL • PLAY THE REEL •</textPath>
    </text>
  </svg>
);

export const Reel = () => {
  const ytId = youtubeId(reel.youtube);
  const video = useRef(null);
  const [playing, setPlaying] = useState(false);
  const play = () => { setPlaying(true); video.current?.play()?.catch(() => setPlaying(false)); };

  return (
    <section id="reel" className="relative bg-[#05325F] py-24 text-white md:py-32" data-testid="demo-reel-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-14 grid gap-8 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-7">
            <SectionLabel index="01" light>Demo reel</SectionLabel>
            <h2 className="text-4xl font-bold leading-[1.05] tracking-[-0.03em] sm:text-5xl lg:text-6xl">Fifty seconds of<br />creation.</h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <p className="text-base leading-relaxed text-[#D6E4F2] md:text-lg">A cut of animation, illustration and 3D work while at Green Fox Studio.</p>
          </Reveal>
        </div>

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }}>
        <motion.div variants={{ hidden: { clipPath: "inset(10% 6% 10% 6% round 40px)" }, show: { clipPath: "inset(0% 0% 0% 0% round 28px)", transition: { duration: 1.4, ease: EASE } } }}
          className="group relative aspect-video overflow-hidden rounded-[28px] bg-[#03203E]">
          {ytId ? (
            playing ? (
              <iframe src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1&rel=0`} title="Demo reel"
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen
                className="absolute inset-0 h-full w-full" data-testid="demo-reel-youtube" />
            ) : (
              <img src={reel.poster} alt="Demo reel poster" className="h-full w-full object-cover" />
            )
          ) : (
            <video ref={video} src={reel.src} poster={reel.poster} controls={playing} playsInline preload="metadata"
              className="h-full w-full object-cover" onEnded={() => setPlaying(false)} data-testid="demo-reel-video" />
          )}
          {!playing && (
            <button onClick={play} data-testid="demo-reel-play-button" aria-label="Play demo reel"
              className="absolute inset-0 flex items-center justify-center bg-[#05325F]/25 transition-colors hover:bg-[#05325F]/10">
              <span className="relative flex h-32 w-32 items-center justify-center sm:h-40 sm:w-40">
                <RotatingBadge />
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-[#05325F] transition-transform duration-500 group-hover:scale-110 sm:h-20 sm:w-20">
                  <Play className="ml-1 h-7 w-7 fill-current" />
                </span>
              </span>
            </button>
          )}
          {!playing && <span className="pointer-events-none absolute left-5 top-5 rounded-full bg-white/90 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#05325F]">Hype Reel</span>}
        </motion.div>
        </motion.div>

        <div className="mt-10 grid gap-px overflow-hidden rounded-2xl bg-white/15 sm:grid-cols-2 lg:grid-cols-4">
          {reel.chapters.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08} className="bg-[#05325F] p-6 transition-colors hover:bg-[#09549F]" data-testid={`reel-chapter-${i}`}>
              <p className="text-sm font-bold tracking-widest text-[#8EC5EA]">{c.time}</p>
              <p className="mt-3 text-lg font-bold">{c.title}</p>
              <p className="mt-1 text-sm text-[#D6E4F2]">{c.tag}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
